import {IElementWedlet, IWedlet, WEDLET} from "back/edit/wed/wedlets/wedlet";
import {DOM, JSX} from "lib/commons/xml/dom";
import {IJmlNode, IJmlSubSet} from "lib/commons/xml/jml";
import {BoxInput} from "back/edit/wed/wedlets/box/boxTags";
import {IPopupAutoCompleteFinder, PopupAutoComplete} from "back/commons/widgets/popups";
import {XA} from "lib/commons/xml/xAddr";
import {WedMgr} from "back/edit/wed/wedEditor";
import {CellBuilderLabel} from "back/commons/widgets/grid-libs";
import {DEBUG_TOOLTIP} from "back/commons/widgets/popupable";
import {DOMSH} from "lib/commons/xml/domsh";


/**
 * Fournisseur des valeurs pour une auto-completion.
 */
export interface IAutoCompleteProvider {

	/**
	 * La promise peut retourner null (abandon) si une nouvelle requete a été effectuée avant le retour de cette promise.
	 *
	 * @param newSession indicateur qu'on entame une nouvelle session d'affichage d'une autoCompletion. Compromis d'optimisation
	 * 	pour considérer que le contexte extérieur à cet input est stable au cours de cette "éphémère session" évitant de réinterroger
	 * 	un service couteux / distant. Au cours d'une même session, le widget ne change pas, mais le text et surtout la sélection
	 * 	peut évoluer à chaque appel.
	 */
	findAutoComplete(wigdet: IElementWedlet, text: string, startSel: number, endSel: number, newSession: boolean): Promise<IAutoCompleteEntry[] | null>
}

export interface IAutoCompleteEntry {
	autoCompLabel: string
	autoCompDetails?: string | HTMLElement | (() => string | HTMLElement)
	autoCompExec?: {
		insertText: string
		startReplace: number
		endReplace: number
		startSelAfter?: number
		endSelAfter?: number
	}
	customExec?: (this: IAutoCompleteEntry, wedlet: IWedlet) => void
}

/**
 * Widget pour un menu d'auto-completion pendant la frappe dans un champs de type BoxInput.
 * Dans le WED, cet élément box-autocomplete doit être placé en frère suivant du box-input (ou
 * d'un autre élément héritant de BoxInput).
 *
 * Exemple de free config dans le wedlet :
 * 	<sm:tag refCodes="subTi">
			<sm:openEdtWidget>
				<sm:content>
					<box-ctn-input class="h" skin="box/head-body" skinOver="box/head-body/block " hv="auto" wed-name="subTi#">
						<div class="head">
							<box-label class="label"/>
						</div>
						<div class="body">
							<wed:children>
								<wed:display nodeType="text" wedlet="Box">
									<box-static>
										<box-input wed-name="subTi#-input"/>
										<box-autocomplete inject-btn="">
											<e k="aaa"/>
											<e k="bbb" d="info details"/>
										</box-autocomplete>
									</box-static>
								</wed:display>
							</wed:children>
						</div>
					</box-ctn-input>
				</sm:content>
			</sm:openEdtWidget>
		</sm:tag>
 */
class BoxAutoComplete extends HTMLElement implements IElementWedlet, IPopupAutoCompleteFinder<IAutoCompleteEntry> {
	provider: IAutoCompleteProvider;
	wedlet: IWedlet;

	injectBtn: boolean;

	get boxInput(): BoxInput {
		if (!this._input) this._input = DOM.findPreviousSibling(this, (n: Node): n is BoxInput => {return n instanceof BoxInput})
		return this._input;
	}

	protected _input: BoxInput;

	protected _currentSearch: Promise<IAutoCompleteEntry[] | null>;

	configWedletElt(tpl: Element, wedlet: IWedlet): void {
		this.wedlet = wedlet;
		this.provider = findAutoCompProviderFromTpl(tpl, this);
		const wedMgr = wedlet.wedMgr;
		const boxInput = this.boxInput;
		if (!boxInput || !this.provider) return;

		//Input et provider bien trouvé, création de la popup sur les events

		function onPopupClose(ev: CustomEvent) {
			const entry = ev.detail.returnValue as IAutoCompleteEntry | null;
			if (entry == null || !WEDLET.isWritableWedlet(wedlet)) return;
			const exec = entry.autoCompExec;
			if (exec) {
				if (wedlet.isVirtual()) {
					WEDLET.insertDatasFromDisplay(wedlet, null, exec.insertText);
				} else {
					wedMgr.docHolder.newBatch().spliceSequence(XA.append(wedlet.wedAnchor, exec.startReplace), exec.endReplace - exec.startReplace, exec.insertText).doBatch();
				}
			} else if (entry.customExec) {
				entry.customExec(wedlet)
			}
		}

		boxInput.addEventListener("focusin", (ev: Event) => {
			const popupCreation = PopupAutoComplete.createFromInputEvent({
				input: boxInput,
				finder: this,
				autoSelOnShow: !boxInput.multiline
			}, {
				reg: wedMgr.reg,
				cellBuilder: CELL_BUILDER
			});

			popupCreation.then((popup) => {
				popup.addEventListener("c-beforeclose", onPopupClose);
				boxInput.addEventListener("focusout", (ev) => {
					//Close popup
					// TODO ev.relatedTarget renvoie null surement à cause de la fermeture qui survient un peu avant l'arrivé du focusOut
					// TODO Difficulté à déterminer si le focusout concerne la popup
					//if (ev.relatedTarget && ev.relatedTarget != popup) {
					popup.removeEventListener("c-beforeclose", onPopupClose);
					if (!DEBUG_TOOLTIP) popup.close();
					//}
				}, {once: true});
			});
		});

		this.injectBtn = tpl.hasAttribute("inject-btn");
		if (this.injectBtn) {
			//XXX Api sur BoxInput à ajouter pour injecter un bouton
			const btn = boxInput.shadowRoot.appendChild(<span id="replaceAll" style="background: center center no-repeat url(/@skin@/edit/wed/autoComplete.svg); width:.8em;"/>);
			btn.onpointerdown = function (this: HTMLElement, ev) {
				ev.stopImmediatePropagation();
				ev.preventDefault();
				const boxInput = DOMSH.findHost<BoxInput>(this);
				document.getSelection().empty(); //Pour forcer un selectionchange au boxInput.setCaretAt(0);
				boxInput.setCaretAt(0);
				boxInput.focusableElement.focus();
			};
		}
	}

	async findAutoComplete(newSession = false, ev?: Event): Promise<IAutoCompleteEntry[] | null> {
		const boxInput = this.boxInput;
		const fullText = boxInput.getXmlText();
		const sel = boxInput.getSel();
		try {
			const currentSearch = this._currentSearch = this.provider.findAutoComplete(this, fullText, sel[0], sel[1], newSession);
			const entries = await currentSearch;
			if (currentSearch !== this._currentSearch) return null; //raceCond.
			this._currentSearch = null;
			if (!this.isConnected) return null; //raceCond.
			return entries;
		} catch (e) {
			return [];
		}
	}

	get autoCompleteAnchor(): HTMLElement | ClientRect {
		if (!this.injectBtn && this.boxInput.focusableElement.isContentEditable) {
			const sel = this.boxInput.shadowRoot.getSelection();
			if (sel.type === "None") return this.boxInput;
			const range = sel.getRangeAt(0);
			return range.getBoundingClientRect();
		}
		return this.boxInput;
	}

	refreshBindValue(val: IJmlNode | string | null, children?: IJmlSubSet) {}
}

window.customElements.define("box-autocomplete", BoxAutoComplete);

const CELL_BUILDER = new CellBuilderLabel('autoCompLabel').setDescriptionFunc((row) => row.getData('autoCompDetails'));

export function findAutoCompProviderFromTpl(tpl: Element, widget: IElementWedlet): IAutoCompleteProvider {
	let provider = (tpl as any).autoCompProvider as IAutoCompleteProvider;
	if (!provider) {
		const svcCd = tpl.getAttribute("svc");
		if (svcCd) {
			//Un svc étant issu du wedMgr.reg, il ne peut pas être directement mutualisé sur tpl, mais uniquement pour un même wedMgr et ce tpl.
			const wedMgr = widget.wedlet.wedMgr;
			const dict = wedMgr.getDatasForModel(tpl);
			provider = dict['acProv'];
			if (provider == null) {
				const svc: (tpl: Element, wedMgr: WedMgr) => IAutoCompleteProvider = wedMgr.reg.getSvc(svcCd);
				if (svc) {
					provider = svc(tpl, wedMgr);
				} else {
					console.warn("BoxAutoComplete svc not found: " + svcCd);
				}
				dict['acProv'] = provider;
			}
			return provider;
		}
		//TODO url externe
		//const url = tpl.getAttribute("src");
		//if (url) {
		//  provider = new AutoCompProviderFromUrl(widget.wedlet.wedMgr.reg, url, tpl.getAttribute("credentials") as RequestCredentials);
		//}
		if (!provider) provider = new WedAutoCompProvider(tpl);
		(tpl as any).autoCompProvider = provider //Mutualisation
	}
	return provider;
}


/**
 * Implémentation par défaut construisant l'énumération à partir d'un contenu XML issu du wed.
 * schéma :
 * <{any}>
 *   <e k="textToMatchAndReplace" d="Description/détail"/> // schema e/@k pour compat enum avec WedEnumProvider
 *   TODO <e k="textToReplace" p="regexp de matching" d="Description/détail"/> //
 * </{any}>
 */
export class WedAutoCompProvider implements IAutoCompleteProvider {

	/** Match tout le texte ou juste le mot correspondant à startSel. */
	scopeMode: 'all' | 'word';

	/** Mode de matching en absence d'un pattern propre à l'entrée. */
	matchMode: 'start' | 'contains'; //todo | 'camelCase'

	makers: IAutoCompMaker[];

	constructor(rootEntries?: Element) {
		if (rootEntries) this.buildMakersFromTpl(rootEntries);
	}

	buildMakersFromTpl(rootEntries?: Element) {
		this.scopeMode = rootEntries.getAttribute("scopeMode") as 'all' | 'word' || "all";
		this.matchMode = rootEntries.getAttribute("matchMode") as 'start' | 'contains' || "start";
		this.makers = [];
		for (let ch = rootEntries.firstElementChild; ch; ch = ch.nextElementSibling) {
			const text = ch.getAttribute("k");
			if (text) this.makers.push(new WordAutoCompMk(text, ch.getAttribute("d")));
		}
	}

	async findAutoComplete(wigdet: IElementWedlet, text: string, startSel: number, endSel: number, newSession: boolean): Promise<IAutoCompleteEntry[] | null> {
		const result: IAutoCompleteEntry[] = []
		const state = this.newState(text, startSel, endSel);
		if (state) {
			for (let mk of this.makers) mk.populateEntries(result, state, this);
			if (this.scopeMode === "all" && result.length === 1 && startSel === text.length && text === result[0].autoCompLabel) return []; //Cas particulier, 1 seule proposition identique au text, on suppr le popup.
		}
		return result;
	}

	protected newState(text: string, startSel: number, endSel: number): IAutoCompState | null {
		if (this.scopeMode === "all") {
			return {
				fullText: text,
				startSel,
				endSel,
				match: text.substring(0, startSel),
				startReplace: 0,
				endReplace: text.length,
			}
		}
		//scopeMode: 'word'
		if (startSel !== endSel) return null; //si sel, on abandonne
		let start = startSel;
		while (start > 0) {
			if (!this.isWordChar(text.charCodeAt(--start))) {
				start++;
				break;
			}
		}
		if (start === startSel) return {
			fullText: text,
			startSel,
			endSel,
			match: "",
			startReplace: start,
			endReplace: start,
		};
		let end = startSel;
		while (end < text.length) {
			if (!this.isWordChar(text.charCodeAt(end))) break;
			end++;
		}
		return {
			fullText: text,
			startSel,
			endSel,
			match: text.substring(start, startSel),
			startReplace: start,
			endReplace: end,
		}
	}

	isWordChar(c: number) {
		//chiffre ||  @ a-z [ \ ] ^ _ ` A-Z || char non ascii sauf espace inséccable
		return (c >= 48 && c <= 57) || (c >= 64 && c <= 122) || (c > 127 && c !== 160);
	}
}


export interface IAutoCompState {
	fullText: string
	startSel: number,
	endSel: number,
	match: string,
	startReplace: number
	endReplace: number
}


export interface IAutoCompMaker {
	populateEntries(results: IAutoCompleteEntry[], state: IAutoCompState, provider: WedAutoCompProvider): void
}

/**
 * Remplacement du mot isolé par IAutoCompState.match
 */
export class WordAutoCompMk implements IAutoCompMaker {

	constructor(public label: string, public desc?: string | HTMLElement | (() => string | HTMLElement)) {}

	populateEntries(results: IAutoCompleteEntry[], state: IAutoCompState, provider: WedAutoCompProvider) {
		if (this.isMatch(state, provider)) {
			results.push({
				autoCompLabel: this.label,
				autoCompDetails: this.desc,
				autoCompExec: {
					insertText: this.buildInsertText(state, provider),
					startReplace: state.startReplace,
					endReplace: state.endReplace
				}
			});
		}
	}

	isMatch(state: IAutoCompState, provider: WedAutoCompProvider) {
		switch (provider.matchMode) {
		case "start":
			return this.label.startsWith(state.match);
		case "contains":
			return this.label.indexOf(state.match) >= 0;
		}
		return false;
	}

	buildInsertText(state: IAutoCompState, provider: WedAutoCompProvider) {
		//XXX reconstruction pour preserve case si ignorecase ?
		return this.label;
	}
}

/**
 * Remplacement du texte complet, pas seulement du mot isolé par IAutoCompState.match
 */
export class FullReplaceAutoCompMk extends WordAutoCompMk {
	populateEntries(results: IAutoCompleteEntry[], state: IAutoCompState, provider: WedAutoCompProvider) {
		if (this.isMatch(state, provider)) {
			results.push({
				autoCompLabel: this.label,
				autoCompDetails: this.desc,
				autoCompExec: {
					insertText: this.buildInsertText(state, provider),
					startReplace: 0,
					endReplace: state.fullText.length
				}
			});
		}
	}
}